import React, { useCallback, useState } from 'react';
import Box from 'apollo-react/components/Box';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import Typography from 'apollo-react/components/Typography';
import Button from 'apollo-react/components/Button';
import { toast } from 'react-toastify';
import buttonStyles from '../../../components/common/Button.module.css';

export default function ProfileHeaderBar({
  publicHcpId,
  submittedAt,
  profileLastUpdatedAt,
}) {
  const [copyBusy, setCopyBusy] = useState(false);

  const handleCopyId = useCallback(async () => {
    if (!publicHcpId) return;
    setCopyBusy(true);
    try {
      await navigator.clipboard.writeText(String(publicHcpId));
      toast.success('Unique ID copied to clipboard');
    } catch {
      toast.error('Could not copy ID');
    } finally {
      setCopyBusy(false);
    }
  }, [publicHcpId]);

  const idDisplay = publicHcpId ? String(publicHcpId) : null;

  return (
    <Card
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'primary.light',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
        >
          <Box flex="1 1 240px" minWidth={0}>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              fontWeight={600}
            >
              System-assigned unique ID
            </Typography>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{ mb: 0.5, mt: 0.25 }}
            >
              Use this identifier for support requests and official
              correspondence.
            </Typography>
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              gap={1}
              mt={0.5}
            >
              <Typography
                component="div"
                variant="h4"
                color="primary"
                sx={{
                  mb: 0,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                  letterSpacing: '0.02em',
                  wordBreak: 'break-all',
                }}
              >
                {idDisplay || '—'}
              </Typography>
              {idDisplay ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className={`${buttonStyles.button} flex-shrink-0`}
                  disabled={copyBusy}
                  onClick={handleCopyId}
                >
                  Copy ID
                </Button>
              ) : null}
            </Box>
          </Box>
          <Box
            flex="0 1 auto"
            textAlign="right"
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            {submittedAt ? (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                display="block"
              >
                Submitted {new Date(submittedAt).toLocaleString()}
              </Typography>
            ) : null}
            {profileLastUpdatedAt ? (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                display="block"
                sx={{ mt: submittedAt ? 0.5 : 0 }}
              >
                Last profile update{' '}
                {new Date(profileLastUpdatedAt).toLocaleString()}
              </Typography>
            ) : null}
            {!submittedAt && !profileLastUpdatedAt ? (
              <Typography variant="bodySmall" color="text.secondary">
                —
              </Typography>
            ) : null}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
